import { useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaBoxes,
  FaChartLine,
  FaClipboardCheck,
  FaLayerGroup,
  FaShoppingCart,
  FaTruck,
} from "react-icons/fa";
import FloatingNavbar from "../components/FloatingNavbar";
import PremiumFooter from "../components/PremiumFooter";
import "./MarketingPages.css";

const steps = [
  { id: "01", title: "Capture order", desc: "Incoming demand enters a clean, readable queue.", icon: <FaShoppingCart />, badge: "Order intake" },
  { id: "02", title: "Sync inventory", desc: "Stock levels and availability stay aligned.", icon: <FaBoxes />, badge: "Inventory flow" },
  { id: "03", title: "Prepare dispatch", desc: "Packaging and shipping progress is easy to track.", icon: <FaTruck />, badge: "Order movement" },
  { id: "04", title: "Scale business", desc: "Analytics and process clarity support smarter growth.", icon: <FaChartLine />, badge: "Growth track" },
];

export default function WorkflowPage() {
  const [active, setActive] = useState(0);

  return (
    <main className="marketing-page">
      <div className="marketing-shell">
        <FloatingNavbar />
        <section className="hero-grid">
          <div>
            <span className="section-label">Workflow</span>
            <h1>A step-by-step process story for modern commerce operations.</h1>
            <p>
              The workflow page keeps each stage visible and connected, making order management, inventory sync, and
              business scaling feel smooth and intentional.
            </p>
            <div className="hero-actions">
              <Link className="button-link button-link--accent" to="/features">
                Explore Features <FaArrowRight />
              </Link>
              <Link className="button-link" to="/about">
                About the Company
              </Link>
            </div>
          </div>

          <div className="glass-card preview-shell">
            <div className="preview-top">
              <div className="preview-dots"><span /><span /><span /></div>
              <span className="section-label" style={{ margin: 0 }}>Process Preview</span>
            </div>
            <div className="dual-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <article className="panel-card">
                <h3>Order management</h3>
                <p>Every stage stays visible from intake to dispatch.</p>
                <div className="preview-curve" style={{ marginTop: 16 }} />
              </article>
              <article className="panel-card">
                <h3>Inventory sync</h3>
                <p>Stock and fulfillment signals move together cleanly.</p>
                <div className="preview-curve" style={{ marginTop: 16 }} />
              </article>
            </div>
          </div>
        </section>

        <section className="section-block timeline-layout">
          <aside className="timeline-sticky">
            <div className="section-copy">
              <span className="section-label">Timeline</span>
              <h2>A sticky process rail keeps each step anchored while users scroll.</h2>
            </div>
            <div className="timeline-grid">
              {steps.map((step, index) => (
                <button
                  type="button"
                  className="timeline-card"
                  key={step.id}
                  onMouseEnter={() => setActive(index)}
                  onFocus={() => setActive(index)}
                >
                  <div className="timeline-index">{step.id}</div>
                  <div className="timeline-copy">
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </aside>

          <div className="workflow-grid">
            {steps.map((step, index) => (
              <article className="panel-card" key={step.title}>
                <div className="workflow-panel">
                  <div>
                    <span className="section-label">Step {step.id}</span>
                    <h3 style={{ marginTop: 16 }}>{step.title}</h3>
                    <p>{step.desc}</p>
                  </div>
                  <div className="workflow-visual">
                    <div className="card-icon" style={{ position: "absolute", top: 18, left: 18, marginBottom: 0 }}>
                      {step.icon}
                    </div>
                    <div className="workflow-badge">{active === index ? step.badge : "Connected"}</div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="section-block">
          <div className="section-copy">
            <span className="section-label">Inventory flow</span>
            <h2>Inventory visuals stay calm, even as the process gets busy.</h2>
          </div>
          <div className="dual-grid">
            <article className="panel-card">
              <h3>Stock movement</h3>
              <p>Soft gradients and clean bars show stock health without visual noise.</p>
              <div className="preview-bars" style={{ marginTop: 16, height: 170 }}>
                <span style={{ height: "40%" }} />
                <span style={{ height: "60%" }} />
                <span style={{ height: "48%" }} />
                <span style={{ height: "82%" }} />
                <span style={{ height: "66%" }} />
                <span style={{ height: "90%" }} />
              </div>
            </article>
            <article className="panel-card">
              <h3>Order management flow</h3>
              <p>Dispatch and fulfillment remain readable from start to finish.</p>
              <div className="preview-curve" style={{ marginTop: 16 }} />
              <div className="dashboard-grid" style={{ marginTop: 16 }}>
                <div className="metric-card"><strong>Packed</strong><span>Current state</span></div>
                <div className="metric-card"><strong>Ready</strong><span>Next handoff</span></div>
              </div>
            </article>
          </div>
        </section>

        <section className="section-block">
          <div className="section-copy">
            <span className="section-label">Scaling business</span>
            <h2>Growth stays controlled when the process stays coherent.</h2>
          </div>
          <div className="feature-grid">
            <article className="feature-card">
              <div className="card-icon"><FaClipboardCheck /></div>
              <div className="card-copy">
                <h3>Structured execution</h3>
                <p>Tasks move in sequence with fewer handoff issues.</p>
              </div>
            </article>
            <article className="feature-card">
              <div className="card-icon"><FaLayerGroup /></div>
              <div className="card-copy">
                <h3>Layered growth</h3>
                <p>Every new step fits into the same premium operating model.</p>
              </div>
            </article>
            <article className="feature-card">
              <div className="card-icon"><FaChartLine /></div>
              <div className="card-copy">
                <h3>Business momentum</h3>
                <p>Clear metrics support the next stage of expansion.</p>
              </div>
            </article>
          </div>
        </section>

        <PremiumFooter />
      </div>
    </main>
  );
}
